import { Box, CircularProgress } from "@mui/material";
import { useForkRef } from "@mui/material/utils";
import { forwardRef } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";

// Props are optional because MUI Autocomplete's ListboxProps type is HTMLAttributes<HTMLUListElement>
export interface InfiniteScrollListboxProps extends React.HTMLAttributes<HTMLUListElement> {
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  loading?: boolean;
}

const defaultInfiniteScrollListboxProps = {
  onLoadMore: () => {},
  hasNextPage: false,
  loading: false,
} as const;

export const InfiniteScrollListbox = forwardRef<HTMLUListElement, InfiniteScrollListboxProps>(
  (props, ref) => {
    const { children, onLoadMore, hasNextPage, loading, ...rest } = {
      ...defaultInfiniteScrollListboxProps,
      ...props,
    };

    const [sentinelRef, { rootRef }] = useInfiniteScroll({
      loading,
      hasNextPage,
      onLoadMore,
      // Load next page 200px before reaching the bottom of the list
      rootMargin: "0px 0px 200px 0px",
    });

    const mergedRef = useForkRef(ref, rootRef);

    return (
      <Box component="ul" {...rest} ref={mergedRef}>
        {children}
        {hasNextPage && (
          <Box
            ref={sentinelRef}
            aria-hidden
            component="li"
            sx={{ display: "flex", justifyContent: "center", py: 1 }}
          >
            <CircularProgress size={20} sx={{ visibility: loading ? "visible" : "hidden" }} />
          </Box>
        )}
      </Box>
    );
  }
);
